import { AutoComplete, AutoCompleteProps } from 'primereact/autocomplete';
import { Button } from 'primereact/button';
import { Inplace, InplaceContent, InplaceDisplay } from 'primereact/inplace';
import { classNames } from 'primereact/utils';
import { useRef, useState } from 'react';

import { ITag } from '../../../shared/interfaces/ITag';
import styles from './TagsInplaceEditWithAutoComplete.module.css';

export type TagsInplaceEditWithAutoCompleteProps = Omit<AutoCompleteProps, 'name'> & {
  buttonLabel: string;
  options: ITag[];
  selectedOptions: ITag[];
  assignTagToEntity: (assignTag: Partial<ITag>) => Promise<void>;
};

const TagsInplaceEditWithAutoComplete = ({
  buttonLabel,
  options,
  selectedOptions,
  assignTagToEntity,
  ...props
}: TagsInplaceEditWithAutoCompleteProps) => {
  const [filteredOptions, setFilteredOptions] = useState<ITag[]>(options);
  const [itemSelected, setItemSelected] = useState<Partial<ITag>>();
  const [active, setActive] = useState<boolean>(false);
  const autoCompleteRef = useRef<AutoComplete | null>(null); // Create a ref for the AutoComplete component

  // Custom template for items with tick icon
  const itemTemplate = (option: ITag) => {
    const isSelected = selectedOptions.some((selectedItem) => {
      return selectedItem.name === option.name;
    });

    return (
      <div className='flex justify-content-between align-items-center w-full'>
        <span className={`${isSelected ? 'font-medium' : 'font-normal'}`}>{option.name}</span>
        {isSelected && <i className='pi pi-check text-primary' />}
      </div>
    );
  };

  const onCompleteMethod = (event: { query: string }) => {
    const filteredValues = options.filter((item) => {
      return item.name.toLowerCase().includes(event.query.toLowerCase());
    });
    setFilteredOptions([...filteredValues]);
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    // Programmatically trigger the search method when the input gains focus
    if (autoCompleteRef.current) {
      // We can pass an empty string to show all suggestions initially
      autoCompleteRef?.current?.search(e, '', 'dropdown');
    }
  };

  return (
    <div className={classNames(styles['tags-inplace-edit'], 'w-full')}>
      <Inplace
        active={active}
        onToggle={(e) => {
          setActive(e.value);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            setActive(true);
            if (itemSelected?.name) {
              assignTagToEntity(itemSelected);
            }
            setActive(false);
            setItemSelected(undefined);
          } else if (e.key === 'Escape') {
            setActive(false);
            setItemSelected(undefined);
          }
        }}
        className='py-2'
      >
        {/* The component to show when input field is closed */}
        <InplaceDisplay>
          <Button
            text
            className={classNames('px-0', styles['tags-inplace-edit'])}
            label={buttonLabel}
            onClick={() => {
              setActive(true);
            }}
            icon='pi pi-plus-circle'
          />
        </InplaceDisplay>
        {/* The component to show when input field is opened */}
        <InplaceContent>
          <div className='flex w-full align-items-center gap-2 relative py-2 p-input-icon-left search-autocomplete-container'>
            <span
              className={classNames(
                styles['tags-inplace-edit'],
                'p-float-label p-input-icon-left h-full w-full',
              )}
            >
              <i className='pi pi-search z-1 text-900' />
              <AutoComplete
                suggestions={filteredOptions}
                ref={autoCompleteRef}
                completeMethod={onCompleteMethod}
                placeholder={props.placeholder}
                value={itemSelected}
                field='name'
                itemTemplate={itemTemplate}
                onChange={(e) => {
                  if (e.value.id) {
                    setItemSelected(e.value);
                  } else {
                    setItemSelected({ name: e.value });
                  }
                }}
                onFocus={handleFocus}
                className='w-full'
              />
            </span>

            <div className='flex gap-2 absolute right-0 justify-content-center align-items-center h-full px-2 h-auto'>
              <Button
                icon='pi pi-check'
                className='p-1 w-auto aspect-square'
                onClick={() => {
                  if (itemSelected?.name) {
                    assignTagToEntity(itemSelected);
                  }
                  setActive(false);
                  setItemSelected(undefined);
                }}
              />
              <Button
                icon='pi pi-times'
                className='bg-gray-600 p-1 w-auto aspect-square'
                onClick={() => {
                  setActive(false);
                  setItemSelected(undefined);
                }}
              />
            </div>
          </div>
        </InplaceContent>
      </Inplace>
    </div>
  );
};

export default TagsInplaceEditWithAutoComplete;
